/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { Trans } from 'react-i18next'
import styled, { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { reatomCreateReferralCode } from '@root/features/referral/atoms/reatom-create-referral-code'
import { useTranslation } from '@root/shared/localization'
import { Dialog, Separator } from '@root/shared/ui-kit'
import { DialogAnnouncement } from '@root/shared/ui-kit/dialog/dialog-announcement'

import { ReferralCodeForm } from '../common/referral-code-form'
import { ReferralPointsInfo } from '../common/referral-points-info'

import type { ReferralCodeSubmitValues } from '../common/referral-code-form'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const CREATE_REFERRAL_CODE_PRICE = 0.0098

export const CreateReferralCodeDialog = ({ onOpenChange, open }: Props) => {
  const { t } = useTranslation()
  const [selectedNetwork] = useAtom(selectedNetworkAtom)
  const model = useMemo(() => reatomCreateReferralCode(), [])
  const [requestState] = useAtom(model.requestState)
  const execute = useAction(model.create)

  const handleSubmit = (values: ReferralCodeSubmitValues) => {
    execute({
      referralCode: values.referralCode,
      walletId: values.wallet.id,
    })
  }

  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onOpenChange(false)
    }
  }, [requestState, onOpenChange])

  return (
    <Root
      withClose
      open={open}
      onOpenChange={onOpenChange}
      describedBy={t('referral.invite_referral')}
    >
      <DialogAnnouncement
        css={css`
          --announcement-header-background: var(--appearanceA-7);
          --announcement-body-background: var(--background-1);
        `}
        withSeparator
        header={
          <Header>
            <Title>{t('referral.create-title')}</Title>
            <SubTitle>
              <Trans
                i18nKey="referral.create-subtitle"
                components={[<strong key={0} />]}
              />
            </SubTitle>
            <ReferralPointsInfo
              css={css`
                margin-block: 10px;
              `}
            />
            <Separator />
            <Price>
              <PriceTitle>{t('referral.create-price-title')}</PriceTitle>
              <PriceCost>
                {CREATE_REFERRAL_CODE_PRICE}{' '}
                {Network.toDisplayString(selectedNetwork).toUpperCase()}
              </PriceCost>
            </Price>
          </Header>
        }
      >
        <Form
          device="desktop"
          onCancel={() => onOpenChange(false)}
          state={requestState}
          onSubmit={handleSubmit}
        />
      </DialogAnnouncement>
    </Root>
  )
}

const Root = styled(Dialog)`
  width: 400px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 12px;
  position: relative;
  text-align: center;
  gap: 16px;
`

const Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  line-height: normal;
  line-height: 140%;
`
const SubTitle = styled.h3`
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  color: var(--textA-9);

  strong {
    color: var(--textA-11);
  }
`
const Price = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const PriceTitle = styled.div`
  color: var(--textA-10);
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
`

const PriceCost = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`

const Form = styled(ReferralCodeForm)`
  padding: 20px 12px;
`
